/**
 * Canonical RBAC permission identifiers, mirrored from the backend
 * catalog (backend/crates/shared-rbac/src/catalog.rs). These dotted
 * strings are the authority the api-server's `#[require_permission(...)]`
 * macros check; the dashboard re-states the subset it gates on here, in
 * ONE place, so a typo or a backend rename is a single-line change
 * rather than a silent always-false gate scattered across components.
 *
 * This mirrors the established `BUILTIN_PROVIDER_TAGS` pattern: a finite
 * FE/BE-shared string vocabulary kept in one constant. If a parity test against
 * the shipped catalog is added later, it asserts this object is a subset of it.
 */
export const PERMISSIONS = Object.freeze({
    // Users
    USERS_READ: "tenant.users.read",
    USERS_MANAGE: "tenant.users.manage",
    // Provisioning tokens
    PROVISIONING_TOKENS_READ: "tenant.provisioning_tokens.read",
    PROVISIONING_TOKENS_MANAGE: "tenant.provisioning_tokens.manage",
    TELEMETRY_EXPORT_READ: "tenant.telemetry_export.read",
    TELEMETRY_EXPORT_WRITE: "tenant.telemetry_export.write",
    // Tenant config
    TENANT_CONFIG_READ: "tenant.tenant_config.read",
    TENANT_CONFIG_WRITE: "tenant.tenant_config.write",
    // Tenant-managed coding-agent policy. Read is held by tenant
    // administrators and SecurityEngineering; manage is the mutation gate.
    AGENT_CONFIG_READ: "tenant.agent_config.read",
    AGENT_CONFIG_MANAGE: "tenant.agent_config.manage",
    // LLM provider descriptor (dashboard AI chat). Narrower than
    // TENANT_CONFIG_READ — analyst roles hold it without the admin
    // capture/interception config surface.
    LLM_CONFIG_READ: "tenant.llm_config.read",
    // Endpoints / fleet
    ENDPOINT_METADATA_READ: "tenant.endpoint_metadata.read",
    ENDPOINTS_MANAGE: "tenant.endpoints.manage",
    TELEMETRY_EXECUTE: "tenant.telemetry.execute",
    CLUSTERS_EXECUTE: "tenant.clusters.execute",
    // Directory + installers
    DIRECTORY_DATA_READ: "tenant.directory_data.read",
    INSTALLERS_READ: "tenant.installers.read",
    // Integrations (directory connectors)
    INTEGRATIONS_READ: "tenant.integrations.read",
    INTEGRATIONS_WRITE: "tenant.integrations.write",
    INTEGRATIONS_MANAGE: "tenant.integrations.manage",
    INTEGRATIONS_EXECUTE_SYNC: "tenant.integrations.execute.sync",
    // Person-level telemetry visibility grants. Kept separate from directory
    // and user administration: syncing a person is not authority to decide who
    // may read that person's activity.
    GRANTS_READ: "tenant.grants.read",
    GRANTS_MANAGE: "tenant.grants.manage",
    // `request` is sec-ops only; `approve` is independent of it so an approver
    // cannot file what they would decide. A directory-group approver holds
    // `approve` through no role at all, so the decision affordances inside the
    // queue read `tenantWide` off the list response rather than this set.
    ESCALATIONS_REQUEST: "tenant.escalations.request",
    ESCALATIONS_APPROVE: "tenant.escalations.approve",
    ROLES_MANAGE: "tenant.roles.manage",
    // Signals (detection triggers over the prompt stream — the only Library
    // surface usable without analytics, e.g. by SecurityEngineering).
    SIGNALS_READ: "tenant.signals.read",
    // Tenant-stored kernels and canvases. Deploy controls publication and is
    // deliberately independent of the read/write/manage implication chain.
    VIEWS_READ: "tenant.views.read",
    VIEWS_WRITE: "tenant.views.write",
    VIEWS_MANAGE: "tenant.views.manage",
    VIEWS_DEPLOY: "tenant.views.deploy",
    // Events + AI sessions (sensitive data reads)
    EVENTS_READ: "tenant.events.read",
    EVENTS_READ_SENSITIVE: "tenant.events.read.sensitive",
    // Non-sensitive AI session reads: review findings without prose, review
    // headers, and the alert queue's triage state.
    AI_SESSIONS_READ: "tenant.ai_sessions.read",
    AI_SESSIONS_READ_SENSITIVE: "tenant.ai_sessions.read.sensitive",
    // Analyst actions on the security alert queue: status, assignee,
    // incidents, notes. Tenant admins may also act through USERS_MANAGE.
    // Reads of that state ride on AI_SESSIONS_READ.
    ALERT_TRIAGE_WRITE: "tenant.alert_triage.write",
    ALERT_TRIAGE_MANAGE: "tenant.alert_triage.manage",
    // Analytics. The base verb `tenant.analytics.execute` is implied by the
    // grantable `tenant.analytics.execute.raw` (a role holding it can read the
    // analytics schema) — gate Explore visibility on this base verb. There is
    // no aggregate/raw shape split anymore; `execute.raw` authorizes any query
    // shape, with sensitivity governed by the per-resource read gates.
    ANALYTICS_EXECUTE: "tenant.analytics.execute",
    ANALYTICS_EXECUTE_RAW: "tenant.analytics.execute.raw",
});
